import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useLocalStorage } from "@src/appV2/lib/utils/useLocalstorage";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback } from "react";
import { useHistory } from "react-router-dom";

import { SHIFT_DISCOVERY_BONUS_ELIGIBLE_SHIFT_SHOPPING_PATH } from "../paths";
import { useShiftModalsDataContext } from "../useShiftModalsDataContext";
import { BonusEligibleShiftShoppingBanner } from "./BonusEligibleShiftShoppingBanner";
import { DEFAULT_BONUS_ELIGIBLE_SHIFT_SHOPPING_CONFIG } from "./constants";

const BONUS_ELIGIBLE_SHIFT_SHOPPING_BANNER_DISMISSED_KEY_PREFIX =
  "bonus-eligible-shift-shopping-banner-dismissed";

export function BonusEligibleShiftShoppingBannerEntrypoint() {
  const history = useHistory();
  const { userId } = useDefinedWorker();
  const { baseNavigationPath } = useShiftModalsDataContext();

  const bonusEligibleShiftShoppingConfig = useCbhFlag(
    CbhFeatureFlag.BONUS_ELIGIBLE_SHIFT_SHOPPING,
    {
      defaultValue: DEFAULT_BONUS_ELIGIBLE_SHIFT_SHOPPING_CONFIG,
    }
  );

  const { enabled, bonusAmountInDollars, requiredShiftCount, startDate, deadlineDate } =
    bonusEligibleShiftShoppingConfig;

  const [isDismissed, setIsDismissed] = useLocalStorage(
    `${BONUS_ELIGIBLE_SHIFT_SHOPPING_BANNER_DISMISSED_KEY_PREFIX}-${userId}-${deadlineDate}`,
    false
  );

  const shouldShowBanner = enabled && !isDismissed;

  useLogEffect(
    APP_V2_USER_EVENTS.BONUS_ELIGIBLE_SHIFT_SHOPPING_BANNER_VIEWED,
    {
      workerId: userId,
      bonusAmountInDollars,
      requiredShiftCount,
      deadlineDate,
    },
    { enabled: shouldShowBanner }
  );

  const handleBannerClick = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.BONUS_ELIGIBLE_SHIFT_SHOPPING_BANNER_CLICKED, {
      workerId: userId,
      bonusAmountInDollars,
      requiredShiftCount,
      deadlineDate,
    });
    history.push(`${baseNavigationPath}/${SHIFT_DISCOVERY_BONUS_ELIGIBLE_SHIFT_SHOPPING_PATH}`);
  }, [userId, bonusAmountInDollars, requiredShiftCount, deadlineDate, history, baseNavigationPath]);

  const handleBannerDismiss = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.BONUS_ELIGIBLE_SHIFT_SHOPPING_BANNER_DISMISSED, {
      workerId: userId,
      bonusAmountInDollars,
      requiredShiftCount,
      deadlineDate,
    });
    setIsDismissed(true);
  }, [userId, bonusAmountInDollars, requiredShiftCount, deadlineDate, setIsDismissed]);

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <BonusEligibleShiftShoppingBanner
      bonusAmountInDollars={bonusAmountInDollars}
      requiredShiftCount={requiredShiftCount}
      startDate={startDate}
      deadlineDate={deadlineDate}
      onClick={handleBannerClick}
      onDismiss={handleBannerDismiss}
    />
  );
}
